import { Button, Input, Label, Select, Text, toast, usePrompt } from "@medusajs/ui"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useMemo, useState } from "react"
import { money } from "../lib/format"
import { t, tc } from "../lib/i18n"
import { errorMessage, hqApi, type OrderDetail } from "../lib/medusa"
import { exchangeChoices } from "../lib/returns"
import { newRequestId } from "../lib/stock-changes"

const round2 = (n: number) => Math.round(n * 100) / 100
const sizeName = (option: string) => (option && !/^default/i.test(option) ? option : "")

/**
 * A product ADDED to a site order (owners, 21 Oct 2026: "beyond changing or removing a product"). It is
 * Medusa's own order edit, like a change: the size is reserved for this order and the total follows. A
 * customer who already paid owes the difference (settled in a DM); a cash order collects the new total.
 */
export function AddOrderItem({ order, onClose }: { order: OrderDetail; onClose: () => void }) {
  const queryClient = useQueryClient()
  const prompt = usePrompt()
  const [requestId] = useState(newRequestId)
  const profit = useQuery({ queryKey: ["hq", "product-profit"], queryFn: () => hqApi.productProfit() })
  const choices = useMemo(() => exchangeChoices(profit.data), [profit.data])
  const [productId, setProductId] = useState<string | null>(null)
  const product = choices.find((c) => c.id === productId) ?? null
  const [variantId, setVariantId] = useState<string | null>(null)
  const size = product?.sizes.find((s) => s.variant_id === variantId) ?? null
  const [quantity, setQuantity] = useState("1")
  const [priceText, setPriceText] = useState("")
  const count = Number(quantity)
  const validCount = Number.isInteger(count) && count >= 1 && count <= 20
  const ownPrice = priceText.trim() === "" ? null : Number(priceText.replace(",", "."))
  const validPrice = ownPrice == null || (Number.isFinite(ownPrice) && ownPrice >= 0)
  const unit = ownPrice ?? product?.price ?? null
  const added = unit != null && validCount ? round2(unit * count) : null
  const paid = order.payment.refundable > 0
  const ready = !!size && validCount && validPrice && (size.available ?? 0) >= count
  const toName = product && size ? [product.title, sizeName(size.title)].filter(Boolean).join(" · ") : ""

  const save = useMutation({
    mutationFn: () => hqApi.addOrderItem(order.id, { variant_id: size!.variant_id, quantity: count, unit_price: ownPrice, request_id: requestId }),
    onSuccess: (body) => {
      queryClient.setQueryData(["hq", "order", order.id], body.order)
      void queryClient.invalidateQueries({ queryKey: ["hq", "orders"] })
      void queryClient.invalidateQueries({ queryKey: ["hq", "product-profit"] })
      if (body.replay) toast.success(t("Already done."))
      else toast.success([t("Added to the order and reserved."), body.owed > 0 ? t("The customer owes {amount} more.", { amount: money(body.owed) }) : null].filter(Boolean).join(" "))
      onClose()
    },
    onError: (error) => toast.error(errorMessage(error)),
  })

  async function confirm() {
    if (!ready) return
    const ok = await prompt({
      title: t("Add {n} × {name} to {ref}?", { n: count, name: toName, ref: order.ref }),
      description: [
        added != null ? t("It is reserved for this order and the total becomes about {amount}.", { amount: money(round2(order.total + added)) }) : null,
        paid && added ? t("The customer owes about {amount} more: settle it in a DM (cash at hand-over, or a payment link).", { amount: money(added) }) : null,
      ]
        .filter(Boolean)
        .join(" "),
      confirmText: t("Add to the order"),
      cancelText: t("Back"),
    })
    if (ok) save.mutate()
  }

  return (
    <div className="bg-ui-bg-subtle flex w-full flex-col gap-y-2.5 rounded-lg p-3">
      {profit.isLoading && <Text size="xsmall" className="text-ui-fg-muted">{t("Loading…")}</Text>}
      {profit.data && (
        <div className="grid gap-2 sm:grid-cols-2">
          <div className="flex flex-col gap-y-1">
            <Label size="xsmall" weight="plus">{t("Product")}</Label>
            <Select value={productId ?? undefined} onValueChange={(value) => { setProductId(value); setVariantId(null) }}>
              <Select.Trigger><Select.Value placeholder={t("Choose a product")} /></Select.Trigger>
              <Select.Content>
                {choices.map((choice) => <Select.Item key={choice.id} value={choice.id}>{choice.title}</Select.Item>)}
              </Select.Content>
            </Select>
          </div>
          <div className="flex flex-col gap-y-1">
            <Label size="xsmall" weight="plus">{t("Size")}</Label>
            <Select value={variantId ?? undefined} onValueChange={setVariantId} disabled={!product}>
              <Select.Trigger><Select.Value placeholder={t("Choose a size")} /></Select.Trigger>
              <Select.Content>
                {(product?.sizes ?? []).map((s) => (
                  <Select.Item key={s.variant_id} value={s.variant_id} disabled={(s.available ?? 0) < 1}>
                    {`${sizeName(s.title) || t("One size")} · ${tc("stocked", "{n} available", { n: s.available ?? 0 })}`}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select>
          </div>
        </div>
      )}
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-y-1">
          <Label htmlFor="add-qty" size="xsmall" weight="plus">{t("How many")}</Label>
          <div className="w-16"><Input id="add-qty" size="small" inputMode="numeric" value={quantity} onChange={(event) => setQuantity(event.target.value)} aria-invalid={!validCount} /></div>
        </div>
        <div className="flex flex-col gap-y-1">
          <Label htmlFor="add-price" size="xsmall" weight="plus">{t("Price € a piece")}</Label>
          <div className="w-28">
            <Input id="add-price" size="small" inputMode="decimal" value={priceText} onChange={(event) => setPriceText(event.target.value)} placeholder={product?.price != null ? String(product.price) : ""} aria-invalid={!validPrice} />
          </div>
        </div>
      </div>
      <Text size="xsmall" className="text-ui-fg-muted">{t("Leave the price empty for the shop's price.")}</Text>
      {size && validCount && (size.available ?? 0) < count && <Text size="xsmall" className="text-ui-fg-error">{t("Not enough of that size in stock.")}</Text>}
      {added != null && size && <Text size="xsmall" className="text-ui-fg-subtle">{t("New total about {amount}.", { amount: money(round2(order.total + added)) })}</Text>}
      {!paid && size && <Text size="xsmall" className="text-ui-fg-muted">{t("Nothing has been paid yet: the customer pays the new total at hand-over.")}</Text>}
      <div className="flex flex-wrap gap-2">
        <Button size="small" onClick={() => void confirm()} isLoading={save.isPending} disabled={!ready}>{t("Add to the order")}</Button>
        <Button size="small" variant="secondary" onClick={onClose} disabled={save.isPending}>{t("Cancel")}</Button>
      </div>
    </div>
  )
}
